/**
 * Normalizes legacy Splitbee/Title Case event strings into snake_case PostHog events.
 * Dynamic context moves into event properties instead of the event name.
 */
export type NormalizedAnalyticsEvent = {
  event: string;
  properties: Record<string, unknown>;
};

const STATIC_EVENT_MAP: Record<string, string> = {
  "Cookie Banner Accepted": "cookie_banner_accepted",
  "Cookie Banner Declined": "cookie_banner_declined",
  "Cookie Banner Dismissed": "cookie_banner_dismissed",
  "Submit Source Modal Clicked": "onboarding_source_submitted",
  "Skip Source Modal Clicked": "onboarding_source_skipped",
  "Close Source Modal Clicked": "onboarding_source_modal_closed",
  "Generate Short Clicked": "short_generation_started",
  "Generate Short Started": "short_generation_started",
  "Generate Video Scripts Clicked": "video_scripts_generated",
  "Script Box Generate Video Clicked": "script_box_generate_video_clicked",
  "Script Box Edit Clicked": "script_box_edit_clicked",
  "Regenerate Button Clicked": "short_regenerate_clicked",
  "Buy Downloads Button Clicked": "buy_downloads_clicked",
  "Buy More Downloads Button Clicked": "buy_more_downloads_clicked",
  "Edit Image Clicked": "scene_image_edit_clicked",
  "Edit Or Download Modal Opened": "edit_download_modal_opened",
  "Logout Clicked": "logout_clicked",
  "Dashboard Clip Deleted": "dashboard_clip_deleted",
  "Dashboard sidebar Upgrade now": "dashboard_upgrade_clicked",
  "Dashboard Stripe billing portal opened": "dashboard_billing_portal_opened",
  "Dashboard See All Clips Opened": "dashboard_see_all_clips_opened",
  "Cancel Subscription Confirmation Clicked": "subscription_cancel_confirmed",
  "Subscription Slider Clips Purchase Clicked": "subscription_clips_purchase_clicked",
  "Topbar Dismissed Clicked": "topbar_dismissed",
  "Home Generate Shorts Clicked": "home_generate_shorts_clicked",
  "Home Generate Shorts Website Clicked": "home_generate_shorts_website_clicked",
  "Home Generate Shorts Article Clicked": "home_generate_shorts_article_clicked",
  "Home Generate Shorts Describe Clicked": "home_generate_shorts_describe_clicked",
  "Home Generate Audio Shorts Clicked": "home_generate_audio_shorts_clicked",
  "Create Button In Blog Post Clicked": "blog_post_create_clicked",
  "Exit Feedback Survey Shown": "exit_feedback_survey_shown",
  "Exit Feedback Survey Dismissed": "exit_feedback_survey_dismissed",
  "Gameplay preview prepared": "gameplay_preview_prepared",
  "Gameplay video exported": "gameplay_video_exported",
  "Influencer video exported": "influencer_video_exported",
  "Avatar video exported": "avatar_video_exported",
  "Storytelling video exported": "storytelling_video_exported",
  "Crazy AI video exported": "crazy_ai_video_exported",
  "UGC influencer-video done": "influencer_video_generated",
  "UGC talking-avatar composite done": "avatar_video_generated",
  "Consistent characters: added via modal": "storytelling_character_added",
  "Character reference modal opened": "character_reference_modal_opened",
  "Character reference: local file chosen": "character_reference_file_chosen",
  "Character reference: gallery pick": "character_reference_gallery_selected",
  "Character reference: AI image generated": "character_reference_ai_generated",
  "Storytelling: Link to script clicked": "storytelling_link_to_script_clicked",
  "Storytelling: Idea to script clicked": "storytelling_idea_to_script_clicked",
  "Storytelling: Script idea applied": "storytelling_script_idea_applied",
  "Storytelling: Animate scene thumb": "storytelling_scene_animate_clicked",
  "Storytelling: Animate scene thumb (free tier gated)": "storytelling_scene_animate_gated",
  "Audio video exported": "audio_video_exported",
  "Audio: Consistent characters added via modal": "audio_character_added",
  "Audio: Character reference modal opened": "character_reference_modal_opened",
  "Audio: prepare completed": "audio_prepare_completed",
  "Audio: prepare failed": "audio_prepare_failed",
  "Audio: pick segments modal opened": "audio_pick_segments_modal_opened",
  "Audio: pick segments saved": "audio_pick_segments_saved",
  "Audio: batch clip selected": "audio_batch_clip_selected",
  "Audio: download clicked": "audio_download_clicked",
  "Audio: audio file uploaded": "audio_file_uploaded",
  "Audio: audio file removed": "audio_file_removed",
  "Audio: gallery opened": "audio_gallery_opened",
  "Storytelling: Scripts generated from link": "storytelling_scripts_from_link_generated",
  "Storytelling: Script ideas generated": "storytelling_script_ideas_generated",
  "Storytelling LP: Link to script clicked": "storytelling_lp_link_to_script_clicked",
  "Storytelling LP: Idea to script clicked": "storytelling_lp_idea_to_script_clicked",
  "Video Generator: Add media modal": "video_generator_add_media_opened",
  "UGC influencer: open file picker": "influencer_reference_picker_opened",
  "UGC influencer: reference lightbox": "influencer_reference_lightbox_opened",
  "UGC influencer: reference removed": "influencer_reference_removed",
  "UGC influencer: reference added (file)": "influencer_reference_added",
  "UGC background upload file": "avatar_background_uploaded",
  "UGC background: open library modal": "avatar_background_library_opened",
  "UGC talking-avatar replace background slot": "avatar_background_slot_replaced",
  "Crazy-AI: first frame image selected (local preview)": "crazy_ai_first_frame_selected",
  "Crazy-AI: reference image generated (AI)": "crazy_ai_reference_image_generated",
  "Crazy-AI: See examples clicked": "crazy_ai_examples_clicked",
  "Crazy-AI: reference image lightbox": "crazy_ai_reference_lightbox_opened",
  "Crazy-AI: open first frame modal": "crazy_ai_first_frame_modal_opened",
  "Crazy-AI: multiscene reference lightbox": "crazy_ai_multiscene_reference_lightbox_opened",
  "Crazy-AI: multiscene reference removed": "crazy_ai_multiscene_reference_removed",
  "Crazy-AI: open multiscene reference modal": "crazy_ai_multiscene_reference_modal_opened",
  "Influencer: Generate video (free tier gated)": "influencer_generate_gated",
  "Crazy AI: Generate video (free tier gated)": "crazy_ai_generate_gated",
};

type PatternRule = {
  pattern: RegExp;
  event: string;
  propertyKeys?: string[];
};

const PATTERN_RULES: PatternRule[] = [
  { pattern: /^Video Generator Opened: (.+)$/, event: "video_generator_opened", propertyKeys: ["preset"] },
  { pattern: /^Video Generator Regenerate: (.+)$/, event: "video_generator_regenerate_clicked", propertyKeys: ["preset"] },
  { pattern: /^Dashboard Locked Video Card Click: (.+)$/, event: "dashboard_locked_card_clicked", propertyKeys: ["path"] },
  { pattern: /^Dashboard Create Video Card: (.+)$/, event: "dashboard_create_video_clicked", propertyKeys: ["path"] },
  { pattern: /^(.+) Tab Clicked$/, event: "home_tab_clicked", propertyKeys: ["tab"] },
  { pattern: /^(.+) Language Selection Clicked$/, event: "language_selected", propertyKeys: ["language"] },
  { pattern: /^Inspiration clicked: (.+)$/, event: "inspiration_item_clicked", propertyKeys: ["label"] },
  { pattern: /^Free tier upgrade clicked: (.+)$/, event: "free_tier_upgrade_clicked", propertyKeys: ["feature"] },
  { pattern: /^LP (.+): (.+) auth gate$/, event: "landing_page_auth_gate", propertyKeys: ["flow_id", "trigger"] },
  { pattern: /^Homepage Video Type Picker: (.+)$/, event: "video_type_selected", propertyKeys: ["path"] },
  { pattern: /^pricing_plan_select_(.+)$/, event: "pricing_plan_selected", propertyKeys: ["tier_id"] },
  { pattern: /^Exit Feedback Submitted - (.+)$/, event: "exit_feedback_survey_submitted", propertyKeys: ["feedback"] },
  { pattern: /^Storytelling: Art style (.+)$/, event: "storytelling_art_style_selected", propertyKeys: ["style_id"] },
  { pattern: /^Audio: Media type (.+)$/, event: "audio_media_type_selected", propertyKeys: ["media_type"] },
  { pattern: /^Audio: Art style (.+)$/, event: "audio_art_style_selected", propertyKeys: ["style_id"] },
  { pattern: /^Audio: Clip selection (.+)$/, event: "audio_clip_selection_mode_selected", propertyKeys: ["mode"] },
  { pattern: /^Audio: Extract count (\d+)$/, event: "audio_extract_count_selected", propertyKeys: ["count"] },
  { pattern: /^Audio: Full duration (true|false)$/, event: "audio_full_duration_toggled", propertyKeys: ["enabled"] },
  { pattern: /^Audio: Own library media (true|false)$/, event: "audio_own_library_media_toggled", propertyKeys: ["enabled"] },
  { pattern: /^Audio: Consistent characters (true|false)$/, event: "audio_consistent_characters_toggled", propertyKeys: ["enabled"] },
  { pattern: /^Audio: Soundwave (enabled|disabled)$/, event: "audio_soundwave_toggled", propertyKeys: ["enabled"] },
  { pattern: /^Audio: Soundwave alignment (.+)$/, event: "audio_soundwave_alignment_selected", propertyKeys: ["alignment_id"] },
  { pattern: /^UGC influencer duration: (.+)$/, event: "influencer_duration_selected", propertyKeys: ["duration_sec"] },
  { pattern: /^UGC influencer preset pick: (.+)$/, event: "influencer_preset_selected", propertyKeys: ["preset_id"] },
  { pattern: /^UGC background source: (.+)$/, event: "avatar_background_source_selected", propertyKeys: ["source"] },
  { pattern: /^UGC avatar pick: (.+)$/, event: "avatar_selected", propertyKeys: ["avatar_id"] },
  { pattern: /^Crazy AI: (.+) done$/, event: "crazy_ai_video_generated", propertyKeys: ["model"] },
  { pattern: /^Crazy-AI: video model (.+)$/, event: "crazy_ai_model_selected", propertyKeys: ["model_id"] },
  { pattern: /^Crazy-AI: video duration (\d+)s$/, event: "crazy_ai_duration_selected", propertyKeys: ["duration_sec"] },
  { pattern: /^Crazy-AI: multiscene audio (true|false)$/, event: "crazy_ai_multiscene_audio_toggled", propertyKeys: ["enabled"] },
  { pattern: /^Crazy-AI: multiscene reference added \((.+)\)$/, event: "crazy_ai_multiscene_reference_added", propertyKeys: ["kind"] },
  { pattern: /^(.+): Caption animation type (.+)$/, event: "caption_animation_selected", propertyKeys: ["preset", "animation_id"] },
  { pattern: /^(.+): Caption alignment (.+)$/, event: "caption_alignment_selected", propertyKeys: ["preset", "alignment_id"] },
  { pattern: /^(.+): Captions (enabled|disabled)$/, event: "captions_toggled", propertyKeys: ["preset", "enabled"] },
];

function toSnakeCase(value: string): string {
  const hasDollar = value.startsWith("$");
  const base = hasDollar ? value.slice(1) : value;
  const snake = base
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
  return hasDollar ? `$${snake}` : snake;
}

export function normalizeAnalyticsEvent(
  rawEvent: string,
  incomingProperties: Record<string, unknown> = {}
): NormalizedAnalyticsEvent {
  const trimmed = rawEvent.trim();

  if (STATIC_EVENT_MAP[trimmed]) {
    return { event: STATIC_EVENT_MAP[trimmed], properties: { ...incomingProperties } };
  }

  if (/^\$?[a-z][a-z0-9_]*$/.test(trimmed)) {
    return { event: trimmed, properties: { ...incomingProperties } };
  }

  for (const rule of PATTERN_RULES) {
    const match = trimmed.match(rule.pattern);
    if (!match) continue;

    const properties = { ...incomingProperties };
    rule.propertyKeys?.forEach((key, index) => {
      const value = match[index + 1];
      if (value !== undefined) {
        properties[key] = key === "enabled" ? value === "true" : value;
      }
    });

    return { event: rule.event, properties };
  }

  return { event: toSnakeCase(trimmed), properties: { ...incomingProperties, legacy_event_name: trimmed } };
}
